import { ArrowRightLeft, History, PencilLine, Sparkles, TrendingUp, UserPlus } from "lucide-react"
import { comoFechaHora } from "@/lib/fechas"

/**
 * Lo que le ha pasado a la tarea, en orden.
 *
 * ⚠ **Esto es lo que convierte «queda trazabilidad» en algo real.** La auditoría
 * también lo guarda, pero la auditoría no se enseña: sin esta lista, enterarse de
 * por qué el encargo de hace tres días ya no dice lo mismo exigiría pedirle a
 * alguien que mirara una tabla, que en la práctica es no tener rastro.
 *
 * ⚠ **La conversación no está aquí.** Los comentarios se leen seguidos, aparte:
 * mezclarlos con los cambios convierte el rastro en un chat, y entonces no sirve
 * para lo que se hizo.
 */

export type TipoEvento = "CREADA" | "ASIGNADA" | "ESTADO" | "AVANCE" | "CAMPO"

export type Evento = {
  id: number
  actor_id: number
  tipo: TipoEvento
  campo: string | null
  antes: string | null
  despues: string | null
  nota: string | null
  porcentaje: number | null
  creado_en: string
}

const ICONO = {
  CREADA: Sparkles,
  ASIGNADA: UserPlus,
  ESTADO: ArrowRightLeft,
  AVANCE: TrendingUp,
  CAMPO: PencilLine,
} as const

/** Cómo se llama cada campo para quien lee, no para quien lo programó. */
const NOMBRE_CAMPO: Record<string, string> = {
  titulo: "el título",
  descripcion: "la descripción",
  tipo: "el tipo",
  prioridad: "la prioridad",
  vence_en: "el vencimiento",
  estado: "el estado",
  asignado_a: "el responsable",
}

function frase(e: Evento, nombreDe: (id: number | null) => string | undefined): string {
  const vacio = "sin definir"
  switch (e.tipo) {
    case "CREADA":
      return "creó la tarea"
    case "ASIGNADA":
      // Los valores viajan como identificadores; aquí se resuelven a nombres,
      // porque «de 7 a 9» no le dice nada a nadie.
      return `pasó el responsable de ${nombreDe(numeroDe(e.antes)) ?? vacio} a ${
        nombreDe(numeroDe(e.despues)) ?? vacio
      }`
    case "ESTADO":
      return `movió el estado de ${e.antes ?? vacio} a ${e.despues ?? vacio}`
    case "AVANCE":
      return e.porcentaje === null ? "registró un avance" : `registró un avance (${e.porcentaje} %)`
    case "CAMPO":
      return `cambió ${NOMBRE_CAMPO[e.campo ?? ""] ?? e.campo} de «${e.antes ?? vacio}» a «${
        e.despues ?? vacio
      }»`
  }
}

function numeroDe(valor: string | null): number | null {
  if (valor === null || valor === "") return null
  const n = Number(valor)
  return Number.isNaN(n) ? null : n
}

type Props = {
  eventos: Evento[]
  nombreDe: (id: number | null) => string | undefined
}

export function LineaDeTiempo({ eventos, nombreDe }: Props) {
  return (
    <section className="mt-5">
      <h3 className="flex items-center gap-2 text-sm font-medium">
        <History size={14} aria-hidden={true} />
        Historial
        <span className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
          {eventos.length}
        </span>
      </h3>

      {eventos.length === 0 ? (
        <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
          Todavía no hay movimientos.
        </p>
      ) : (
        <ol className="mt-2 space-y-2">
          {eventos.map((e) => {
            const Icono = ICONO[e.tipo]
            return (
              <li key={e.id} data-testid={`evento-${e.id}`} className="flex gap-2">
                <span className="mt-0.5 shrink-0" style={{ color: "var(--fg-muted)" }}>
                  <Icono size={14} aria-hidden={true} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
                    <span className="font-medium" style={{ color: "var(--fg)" }}>
                      {nombreDe(e.actor_id) ?? `Usuario ${e.actor_id}`}
                    </span>{" "}
                    {frase(e, nombreDe)} ·{" "}
                    <span className="tabular-nums">{comoFechaHora(e.creado_en)}</span>
                  </p>
                  {/* El motivo de un cambio, o el texto del avance. Se pinta aparte
                      y con más peso: es lo único que explica POR QUÉ pasó algo. */}
                  {e.nota !== null && e.nota !== "" && (
                    <p
                      className="mt-0.5 border-l-2 pl-2 text-sm"
                      style={{ borderColor: "var(--border)" }}
                    >
                      {e.nota}
                    </p>
                  )}
                </div>
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}
